import React, { useState, useEffect } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import { Typography, Card, CardContent, Button } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { useTranslation } from 'react-i18next'; // Assuming you are using react-i18next for internationalization

// Define the types for the testimonials
interface Testimonial {
  commentFr: string;
  commentEn: string;
  commenter: string;
  note: number;
  dateFr: string;
  dateEn: string;
  source: string;
}

interface TestimonialsProps {
  testimonials: Testimonial[];
  initialNumToShow?: number;
  numToShow?: number;
}

// Use makeStyles to create styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    title: {
      color: "#890608",
      fontSize: 24,
      marginBottom: 40,
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: theme.spacing(3),
    },
    card: {
      background: 'white',
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[2],
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    comment: {
      marginTop: '5px',
      marginBottom: theme.spacing(1),
      fontSize: '1rem',
    },
    commenter: {
      fontWeight: 'bold',
      marginBottom: '5px',
    },
    stars: {
      display: 'flex',
      alignItems: 'center',
    },
    star: {
      color: theme.palette.warning.main,
    },
    date: {
      fontStyle: 'italic',
      fontSize: '0.9rem',
      marginBottom: theme.spacing(1),
    },
    button: {
      marginTop: theme.spacing(2),
    },
  })
);

const Testimonials: React.FC<TestimonialsProps> = ({ testimonials, initialNumToShow = 5, numToShow = 10 }) => {
  const classes = useStyles();
  const { i18n } = useTranslation<'common'>();
  const [showAll, setShowAll] = useState(false);
  const [shuffledTestimonials, setShuffledTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    // Function to shuffle array
    const shuffleArray = (array: Testimonial[]) => {
      let currentIndex = array.length;
      let temporaryValue;
      let randomIndex;

      // While there remain elements to shuffle...
      while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }

      return array;
    };

    // Shuffle the testimonials array once and store in state
    const shuffled = shuffleArray([...testimonials]);
    setShuffledTestimonials(shuffled);
  }, [testimonials]);

  const testimonialsToShow = showAll ? shuffledTestimonials.slice(0, numToShow) : shuffledTestimonials.slice(0, initialNumToShow);

  const handleViewMore = () => {
    setShowAll(!showAll);
  };

  return (
    <div className={classes.container}>
      <div className={classes.cardGrid}>
        {testimonialsToShow.map((testimonial, index) => (
          <Card key={index} className={classes.card}>
            <CardContent>
              <Typography variant="body2" className={classes.commenter}>
                - {testimonial.commenter}
              </Typography>
              <div className={classes.stars}>
                {[...Array(5)].map((_, i) => (
                  i < testimonial.note ? (
                    <StarIcon key={i} className={classes.star} />
                  ) : (
                    <StarBorderIcon key={i} className={classes.star} />
                  )
                ))}
              </div>
              <Typography variant="body1" className={classes.comment}>
                {i18n.language === 'fr' ? `"${testimonial.commentFr}"` : `"${testimonial.commentEn}"`}
              </Typography>
              <Typography variant="body2" className={classes.date}>
                {i18n.language === 'fr' ? testimonial.dateFr : testimonial.dateEn}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </div>
      {testimonials.length > initialNumToShow && (
        <Button className={classes.button} onClick={handleViewMore}>
          {showAll ? (i18n.language === 'fr' ? 'Voir moins' : 'View less') : (i18n.language === 'fr' ? 'Voir plus' : 'View more')}
        </Button>
      )}
    </div>
  );
};

export default Testimonials;
